// src/routes/_authed.history.tsx  (Task 9 implementation)
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../api";
import type { AuthState } from "../auth/AuthProvider";
import { Spinner } from "../login/Spinner";

function HistoryRoute() {
  return <HistoryScreen auth={Route.useRouteContext().auth} />;
}

export const Route = createFileRoute("/_authed/history")({
  component: HistoryRoute,
});

const zar = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });
const date = (iso: string) =>
  new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });

const statusStyles: Record<string, string> = {
  Collected: "bg-teal-100 text-teal-700",
  Cancelled: "bg-slate-200 text-slate-500",
};

// Exported for tests (rendered without a router).
export function HistoryScreen({ auth }: { auth: AuthState }) {
  const visits = useInfiniteQuery({
    queryKey: ["me", auth.user?.uid, "visits"],
    queryFn: ({ pageParam }) => api.visits(auth, pageParam),
    initialPageParam: 1,
    // Schema-driven: totalCount/page/pageSize are typed `number | string` in the generated
    // schema (decimal/int64 serialization); coerce before arithmetic comparison.
    getNextPageParam: (last) =>
      Number(last.page) * Number(last.pageSize) < Number(last.totalCount) ? Number(last.page) + 1 : undefined,
  });

  if (visits.isLoading)
    return <main className="grid min-h-[60vh] place-items-center text-slate-400">Loading…</main>;
  if (visits.isError || !visits.data)
    return (
      <main className="grid min-h-[60vh] place-items-center p-6 text-center text-slate-500">
        <div>
          <p>Something went wrong.</p>
          <button onClick={() => visits.refetch()} className="mt-3 text-teal-600 underline">Try again</button>
        </div>
      </main>
    );

  const items = visits.data.pages.flatMap((p) => p.items);
  return (
    <main className="mx-auto max-w-lg space-y-3 p-4">
      <h1 className="text-lg font-semibold text-slate-800">Your washes</h1>
      {items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500">
          No visits yet — visit a participating carwash and your history will appear here.
        </p>
      ) : (
        items.map((v) => (
          <a
            key={v.visitId}
            href={`/track/${v.trackingToken}`}
            className="block rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-800">{v.businessName}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${statusStyles[v.status] ?? "bg-amber-100 text-amber-700"}`}>
                {v.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {v.siteName} · {date(v.capturedAt)} · {zar.format(Number(v.quotedPrice))}
            </p>
            <p className="text-xs text-slate-400">{v.services.map((s) => s.name).join(", ")}</p>
          </a>
        ))
      )}
      {visits.hasNextPage && (
        <button
          onClick={() => visits.fetchNextPage()}
          disabled={visits.isFetchingNextPage}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 py-3 text-slate-600 disabled:opacity-50"
        >
          {visits.isFetchingNextPage ? (
            <>
              <Spinner size={16} />
              Loading…
            </>
          ) : (
            "Load more"
          )}
        </button>
      )}
    </main>
  );
}
